import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'

import { deepMerge } from '@local/utils'

import { createErrorLink, offlineLink, ResolverContext } from '@web/libs/apollo/client'
import { CLIENT_VERSION, IS_BROWSER, PHASE, X_PORTA_AUTH } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import federationIntrospection from '@web/pages/v5/types/possible-types'

import federationTypePolicies from './federation-typePolicies'

export let federationApolloClient: ApolloClient<NormalizedCacheObject> | undefined

/**
 * Cosmo Studio에서 관리하기 위한 값입니다.
 */
export const customFederationHeader = {
  'GraphQL-Client-Name': 'myplace-web',
  'GraphQL-Client-Version': CLIENT_VERSION,
  'Accept-Language': 'ko-kr',
  'X-Porta-Auth': X_PORTA_AUTH,
}

export const getFederationHeader = (headers: Record<string, string>) => {
  return {
    ...customFederationHeader,
    ...headers,
  }
}

/**
 *
 * 통합 BFF를 바라보는 Apollo Link입니다.
 *
 */
function createFederationLink(context: ResolverContext = {}) {
  const cookie = context.req?.headers?.cookie

  if (IS_BROWSER)
    return new HttpLink({
      uri: `${links.FEDERATION_API_ON_BROWSER}/graphql`,
      credentials: 'include',
      headers: customFederationHeader,
    })

  return new HttpLink({
    uri: `${links.FEDERATION_API_ON_NODE}/graphql`,
    headers: cookie ? { cookie, ...customFederationHeader } : customFederationHeader,
  })
}

function createApolloClient(context?: ResolverContext) {
  const federatedApiLink = createFederationLink(context)
  const errorLink = createErrorLink(context)

  return new ApolloClient({
    ssrMode: !IS_BROWSER,
    link: from([offlineLink, errorLink, federatedApiLink]),
    cache: new InMemoryCache({
      typePolicies: federationTypePolicies,
      possibleTypes: federationIntrospection.possibleTypes,
      addTypename: true,
    }),
    connectToDevTools: PHASE !== 'production',
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
  })
}

interface initializeApolloOption {
  initialState?: NormalizedCacheObject
  // Pages with Next.js data fetching methods, like `getStaticProps`, can send
  // a custom context which will be used by `SchemaLink` to server render pages
  context?: ResolverContext
}

export function initializeFederationApollo({ initialState, context }: initializeApolloOption = {}) {
  const _apolloClient = federationApolloClient ?? createApolloClient(context)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    const existingCache = _apolloClient.extract()
    _apolloClient.cache.restore(deepMerge(existingCache, initialState)) // Merge exisiting cache with data from SSR
  }

  // For SSG and SSR always create a new Apollo Client
  if (!IS_BROWSER) return _apolloClient

  // Create the Apollo Client once in the client
  if (!federationApolloClient) federationApolloClient = _apolloClient

  return _apolloClient
}
